import React, { useMemo, useState } from 'react';
import { ArrowUpRightIcon, BarChart3Icon, ClipboardListIcon, MapPinIcon, MessageSquareTextIcon, PackageCheckIcon, QrCodeIcon, UsersIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AnimatedMetric } from '../components/AnimatedMetric';
import { ResearchSource } from '../components/ResearchSource';
import { useScreenInit } from '../useScreenInit.js';
const navigation = ['Overview', 'Campaigns', 'Audience', 'Feedback'] as const;
const timeframes = ['7 DAYS', '30 DAYS', 'CAMPAIGN'] as const;
type Timeframe = (typeof timeframes)[number];
type Metric = {
  value: number;
  suffix: string;
  decimals?: number;
  label: string;
  event: string;
};
const metricSets: Record<Timeframe, Metric[]> = {
  '7 DAYS': [{
    value: 1840,
    suffix: '',
    label: 'EXPERIENCES STARTED',
    event: 'Experience-start event'
  }, {
    value: 1210,
    suffix: '',
    label: 'PRODUCTS CAUGHT',
    event: 'Catch event'
  }, {
    value: 864,
    suffix: '',
    label: 'REDEMPTIONS',
    event: 'Redemption-scan event'
  }, {
    value: 4.5,
    suffix: ' / 5',
    decimals: 1,
    label: 'EXPERIENCE SCORE',
    event: 'Feedback-response event'
  }],
  '30 DAYS': [{
    value: 12482,
    suffix: '',
    label: 'EXPERIENCES STARTED',
    event: 'Experience-start event'
  }, {
    value: 8721,
    suffix: '',
    label: 'PRODUCTS CAUGHT',
    event: 'Catch event'
  }, {
    value: 6283,
    suffix: '',
    label: 'REDEMPTIONS',
    event: 'Redemption-scan event'
  }, {
    value: 4.6,
    suffix: ' / 5',
    decimals: 1,
    label: 'EXPERIENCE SCORE',
    event: 'Feedback-response event'
  }],
  CAMPAIGN: [{
    value: 18420,
    suffix: '',
    label: 'EXPERIENCES STARTED',
    event: 'Experience-start event'
  }, {
    value: 12810,
    suffix: '',
    label: 'PRODUCTS CAUGHT',
    event: 'Catch event'
  }, {
    value: 9150,
    suffix: '',
    label: 'REDEMPTIONS',
    event: 'Redemption-scan event'
  }, {
    value: 4.6,
    suffix: ' / 5',
    decimals: 1,
    label: 'EXPERIENCE SCORE',
    event: 'Feedback-response event'
  }]
};
const trends: Record<Timeframe, number[]> = {
  '7 DAYS': [30, 48, 35, 58, 46, 74, 62],
  '30 DAYS': [24, 37, 31, 52, 44, 69, 58, 79, 66, 91, 72, 84],
  CAMPAIGN: [18, 28, 38, 34, 52, 48, 64, 59, 76, 70, 86, 80]
};
const funnels: Record<Timeframe, Array<[string, number]>> = {
  '7 DAYS': [['DISCOVERED', 100], ['JOINED', 74], ['CAUGHT', 55], ['REDEEMED', 40], ['RESPONDED', 29]],
  '30 DAYS': [['DISCOVERED', 100], ['JOINED', 76], ['CAUGHT', 58], ['REDEEMED', 42], ['RESPONDED', 31]],
  CAMPAIGN: [['DISCOVERED', 100], ['JOINED', 78], ['CAUGHT', 61], ['REDEEMED', 45], ['RESPONDED', 34]]
};
const audienceSets: Record<Timeframe, Array<[string, number]>> = {
  '7 DAYS': [['18–24', 30], ['25–34', 36], ['35–44', 20], ['45+', 14]],
  '30 DAYS': [['18–24', 28], ['25–34', 38], ['35–44', 21], ['45+', 13]],
  CAMPAIGN: [['18–24', 29], ['25–34', 37], ['35–44', 21], ['45+', 13]]
};
const feedback = [['DISCOVERY EXPERIENCE', 'Easy to find', 'Feedback-response event'], ['COLLECTION CLARITY', 'Clear collection point', 'Feedback-response event'], ['LOCATION COVERAGE', 'More locations requested', 'Feedback-response event']];
const campaigns = [['STOCKHOLM PRODUCT DROP', 'ILLUSTRATIVE LIVE STATE', 'Stockholm'], ['WEEKEND DISCOVERY', 'PLANNING', 'Regional'], ['RETAIL CATCH WINDOW', 'COMPLETE', 'City centre']];
const sectionCopy: Record<(typeof navigation)[number], string> = {
  Overview: 'Read the connected journey from discovery through feedback.',
  Campaigns: 'Review campaign states, scope, and the events a live system could follow.',
  Audience: 'See how a future reporting view could group consented audience information.',
  Feedback: 'Trace response themes back to the experience and product moment.'
};
export function BrandPortalPage() {
  const screenInit = useScreenInit();
  const [activeSection, setActiveSection] = useState<(typeof navigation)[number]>('Overview');
  const [timeframe, setTimeframe] = useState<Timeframe>(screenInit.timeframe as Timeframe ?? '30 DAYS');
  const reduceMotion = useReducedMotion();
  const metrics = metricSets[timeframe];
  const trend = trends[timeframe];
  const funnel = funnels[timeframe];
  const audience = audienceSets[timeframe];
  const timeframeMessage = useMemo(() => `${timeframe} illustrative prototype view selected.`, [timeframe]);
  return <div className="min-h-screen bg-[#f4f1e9] text-[#11110f]" data-id="element-819" data-mp="bbjq6u38laav">
      <header className="border-b border-black/15 bg-[#f7f5ef]" data-id="element-820" data-mp="ba8kupu57uhn">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-4 md:px-8" data-id="element-821" data-mp="7953fywg5qeb">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Sampler home" data-id="element-822" data-mp="2bwp69ef66ic">
            <span className="grid h-8 w-8 place-items-center rounded-sm bg-[#11110f] text-white" data-id="element-823" data-mp="3thelfa416ru6">
              <span className="h-2.5 w-2.5 rounded-full bg-current" data-id="element-824" data-mp="3ohs870o7dbld" />
            </span>
            <span className="font-display text-xl font-bold" data-id="element-825" data-mp="3eemuy3sfmw73">SAMPLER</span>
          </Link>
          <div className="hidden items-center gap-3 text-sm font-bold sm:flex" data-id="element-826" data-mp="3jcy74l679ish">
            <span className="border-r border-black/15 pr-3 text-black/65" data-id="element-827" data-mp="3ocb25t785w65">
              BRAND WORKSPACE
            </span>
            <span data-id="element-828" data-mp="3tbxe7zbbh6dd">DEMO WORKSPACE</span>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-black/20 px-4 py-2 text-xs font-extrabold hover:border-black" data-id="element-829" data-mp="3t9b7u305wwwq">
            PUBLIC SITE <ArrowUpRightIcon size={14} aria-hidden="true" data-id="element-830" data-mp="30ni1xkjkl25q" />
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-5 py-6 md:px-8 md:py-8" data-id="element-831" data-mp="35l1hcns07kz9">
        <div className="border border-[#ff4d17] bg-[#fff4ef] px-4 py-3 text-xs font-bold leading-relaxed text-black/80" role="note" data-id="element-832" data-mp="3acruf1ibsmud">
          <span className="font-extrabold text-[#c9340b]" data-id="element-833" data-mp="2cwmc2syoch14">
            ILLUSTRATIVE PROTOTYPE.
          </span>{' '}
          This workspace demonstrates a possible reporting experience. No
          campaign, demographic, feedback, or performance value represents live
          customer data.
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[210px_1fr]" data-id="element-834" data-mp="24w6ctskjn642">
          <aside className="lg:sticky lg:top-6 lg:h-fit" aria-label="Portal navigation" data-id="element-835" data-mp="2jkawozvc1nof">
            <nav className="flex gap-2 overflow-x-auto border-b border-black/15 pb-3 lg:flex-col lg:border-b-0 lg:pb-0" data-id="element-836" data-mp="2elzmgo14lb5o">
              {navigation.map(item => {
              const isActive = activeSection === item;
              return <button key={item} type="button" aria-pressed={isActive} onClick={() => setActiveSection(item)} className={`whitespace-nowrap border px-4 py-3 text-left text-sm font-bold transition-colors lg:w-full ${isActive ? 'border-[#11110f] bg-[#11110f] text-white' : 'border-black/15 bg-[#f7f5ef] text-black/75 hover:border-black'}`} data-id="element-837" data-mp="1lz535ug1ohts">
                    {item}
                  </button>;
            })}
            </nav>
            <div className="mt-6 hidden border border-black/15 bg-[#f7f5ef] p-4 lg:block" data-id="element-838" data-mp="37d9jkvfst279">
              <p className="text-xs font-extrabold tracking-[0.12em] text-black/55" data-id="element-839" data-mp="3ewisyoob10wb">
                DATA STATUS
              </p>
              <p className="mt-3 text-sm font-bold" data-id="element-840" data-mp="35u174bckaa9u">
                Illustrative campaign-review prototype.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-black/65" data-id="element-841" data-mp="39webavbnu04i">
                Every visual below is a product-direction example, not a
                reporting claim.
              </p>
            </div>
          </aside>

          <main data-id="element-842" data-mp="6dupobnhlsio">
            <div className="flex flex-col gap-5 border-b border-black/15 pb-7 md:flex-row md:items-end md:justify-between" data-id="element-843" data-mp="6ggxmceaia1f">
              <div data-id="element-844" data-mp="3stp90bhn8lhu">
                <p className="text-xs font-extrabold tracking-[0.16em] text-[#ff4d17]" data-id="element-845" data-mp="3st5qoto2zv3a">
                  {activeSection.toUpperCase()} / DEMO WORKSPACE
                </p>
                <h1 className="mt-3 font-display text-5xl font-bold leading-[0.88] md:text-7xl" data-id="element-846" data-mp="p4vrd7fzs25x">
                  Campaign review, not a report.
                </h1>
                <AnimatePresence mode="wait" initial={false} data-id="element-847" data-mp="gknwrqmay5m">
                  <motion.p key={activeSection} initial={reduceMotion ? false : {
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={reduceMotion ? undefined : {
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.2,
                  ease: [0.22, 1, 0.36, 1]
                }} className="mt-4 max-w-2xl text-base leading-relaxed text-black/75" data-id="element-848" data-mp="3rs44go9bmvxx">
                    {sectionCopy[activeSection]} All values and themes are
                    illustrative.
                  </motion.p>
                </AnimatePresence>
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Illustrative timeframe" data-id="element-849" data-mp="2u6yfzx8w4ga5">
                {timeframes.map(item => <button key={item} type="button" aria-pressed={timeframe === item} onClick={() => setTimeframe(item)} className={`rounded-full border px-3 py-2 text-xs font-extrabold transition-colors ${timeframe === item ? 'border-[#ff4d17] bg-[#ff4d17] text-white' : 'border-black/20 bg-[#f7f5ef] hover:border-black'}`} data-id="element-850" data-mp="2p8n5dkaru1qv">
                    {item}
                  </button>)}
              </div>
            </div>
            <p className="sr-only" aria-live="polite" data-id="element-851" data-mp="1oc8xzt0pmfdq">
              {timeframeMessage}
            </p>

            <section className="mt-7" aria-labelledby="performance-heading" data-id="element-852" data-mp="1sdken91lvq9s">
              <div className="flex items-baseline justify-between gap-4" data-id="element-853" data-mp="1sfotun4ygbpd">
                <h2 id="performance-heading" className="font-display text-3xl font-bold" data-id="element-854" data-mp="1xdgjtbm1hnqx">
                  The campaign at a glance.
                </h2>
                <span className="text-xs font-bold text-black/65" data-id="element-855" data-mp="2zfm4u2876sf">
                  ILLUSTRATIVE DATA · {timeframe}
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-856" data-mp="803bzcepxhl2">
                {metrics.map((metric, index) => <motion.article key={metric.label} layout={false} initial={reduceMotion ? false : {
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: reduceMotion ? 0 : index * 0.05,
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} className="border border-black/15 bg-[#f7f5ef] p-5" data-id="element-857" data-mp="l8yx5g0l7nm2" data-mpi={index}>
                    <AnimatedMetric value={metric.value} suffix={metric.suffix} decimals={metric.decimals} ariaLabel={`${metric.label}: ${metric.value}${metric.suffix}, illustrative data`} className="font-display text-4xl font-bold" data-id="element-858" data-mp="1939zr3rop5ox" />
                    <p className="mt-3 text-[10px] font-extrabold tracking-[0.1em] text-black/75" data-id="element-859" data-mp="1iw7c0s631yf3">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-xs font-bold text-black/55" data-id="element-860" data-mp="1rvudtf2q568o">
                      ILLUSTRATIVE · {metric.event.toUpperCase()}
                    </p>
                  </motion.article>)}
              </div>
            </section>

            <section className="mt-7 grid gap-5 xl:grid-cols-[1.25fr_0.75fr]" aria-label="Illustrative performance and campaign funnel" data-id="element-861" data-mp="37gd5ufvefe78">
              <article className="border border-black/15 bg-[#11110f] p-5 text-white md:p-6" data-id="element-862" data-mp="2ye3whtbv9qvf">
                <div className="flex items-start justify-between gap-4" data-id="element-863" data-mp="3v44gzg57dl6x">
                  <div data-id="element-864" data-mp="3r1zqurdyr6p1">
                    <p className="text-xs font-extrabold tracking-[0.12em] text-[#72c7e8]" data-id="element-865" data-mp="3r1hnzbwspu85">
                      PERFORMANCE TREND / PROTOTYPE
                    </p>
                    <h2 className="mt-2 font-display text-3xl font-bold" data-id="element-866" data-mp="3m8y8uaigmr18">
                      Experience activity over time.
                    </h2>
                  </div>
                  <BarChart3Icon className="text-[#ff4d17]" aria-hidden="true" data-id="element-867" data-mp="iniq24q9jys0" />
                </div>
                <div className="mt-8 flex h-48 items-end gap-2" aria-label={`Illustrative activity trend chart for ${timeframe}`} data-id="element-868" data-mp="du6gn1u5nyju">
                  {trend.map((height, index) => <span key={index} className="flex h-full flex-1 items-end overflow-hidden" aria-hidden="true" data-id="element-869" data-mp="11r2sic24cdf2" data-mpi={index}>
                      <motion.span className="h-full w-full origin-bottom bg-[#ff4d17]" animate={{
                    scaleY: height / 100
                  }} transition={{
                    duration: 0.45,
                    delay: reduceMotion ? 0 : index * 0.025,
                    ease: [0.22, 1, 0.36, 1]
                  }} data-id="element-870" data-mp="nymvbrvwf9m2" />
                    </span>)}
                </div>
                <div className="mt-4 flex justify-between text-[10px] font-bold tracking-[0.1em] text-white/65" data-id="element-871" data-mp="51loe1isazfh">
                  <span data-id="element-872" data-mp="92x6zn38qke6">START</span>
                  <span data-id="element-873" data-mp="93yr0h28ih81">MID-CAMPAIGN</span>
                  <span data-id="element-874" data-mp="1zebe0724dmh2">NOW</span>
                </div>
                <p className="mt-4 text-xs font-bold text-white/70" data-id="element-875" data-mp="1zcqnqtmu8pp0">
                  ILLUSTRATIVE EVENT COUNT — a live view would be underpinned by
                  timestamped experience-start events.
                </p>
              </article>

              <article className="border border-black/15 bg-white p-5 md:p-6" data-id="element-876" data-mp="3jxnakas3sb3n">
                <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-877" data-mp="1j63p3vjojkd">
                  CAMPAIGN FUNNEL / PROTOTYPE
                </p>
                <h2 className="mt-2 font-display text-3xl font-bold" data-id="element-878" data-mp="3sv7exsl9inih">
                  What moved forward.
                </h2>
                <div className="mt-7 space-y-4" data-id="element-879" data-mp="6hzg0sz3i4iy">
                  {funnel.map(([label, amount], index) => <div key={label} data-id="element-880" data-mp="khiawk2wbjdb" data-mpi={index}>
                      <div className="flex justify-between text-xs font-extrabold" data-id="element-881" data-mp="kgfl6fhb7myz">
                        <span data-id="element-882" data-mp="fhvvc3zlhr1c">{label}</span>
                        <span data-id="element-883" data-mp="fj70o88ov6uk">ILLUSTRATIVE</span>
                      </div>
                      <div className="mt-2 h-3 overflow-hidden bg-black/10" data-id="element-884" data-mp="2m9mzbll6ryja">
                        <motion.div className="h-full w-full origin-left bg-[#ff4d17]" animate={{
                      scaleX: amount / 100
                    }} transition={{
                      duration: 0.4,
                      delay: reduceMotion ? 0 : index * 0.045,
                      ease: [0.22, 1, 0.36, 1]
                    }} data-id="element-885" data-mp="2haah5e59ffn1" />
                      </div>
                    </div>)}
                </div>
                <p className="mt-6 text-xs font-bold leading-relaxed text-black/60" data-id="element-886" data-mp="2xmvr10df5c35">
                  A future funnel could join experience-start, catch,
                  redemption-scan, and feedback-response events by campaign and
                  time window.
                </p>
              </article>
            </section>

            <section className="mt-7 grid gap-5 lg:grid-cols-2" aria-label="Illustrative audience and feedback review" data-id="element-887" data-mp="29wjax280s91h">
              <article className="border border-black/15 bg-[#f7f5ef] p-5 md:p-6" data-id="element-888" data-mp="1h9yhzxbwbovt">
                <div className="flex items-start justify-between gap-4" data-id="element-889" data-mp="1h847lcy93f5z">
                  <div data-id="element-890" data-mp="1m7gufxp4gm8k">
                    <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-891" data-mp="1m6owwhbysefn">
                      AUDIENCE / PROTOTYPE
                    </p>
                    <h2 className="mt-2 font-display text-3xl font-bold" data-id="element-892" data-mp="19rqxbbdbwb6y">
                      Who joined the experience.
                    </h2>
                  </div>
                  <UsersIcon aria-hidden="true" data-id="element-893" data-mp="zsrnhpgtu9id" />
                </div>
                <div className="mt-7 grid grid-cols-2 gap-3" data-id="element-894" data-mp="zum5auwsxli8">
                  {audience.map(([label, value]) => <div key={label} className="border border-black/15 bg-white p-4" data-id="element-895" data-mp="vshiog4rtp53">
                      <AnimatedMetric value={value} suffix="%" ariaLabel={`${label}: ${value} percent, illustrative data`} className="font-display text-3xl font-bold" data-id="element-896" data-mp="qxv300rnpaf7" />
                      <p className="mt-2 text-xs font-bold" data-id="element-897" data-mp="h3350vu5wljw">{label}</p>
                      <p className="mt-2 text-[10px] font-extrabold text-black/55" data-id="element-898" data-mp="c36zgs5tgm28">
                        ILLUSTRATIVE DATA
                      </p>
                    </div>)}
                </div>
                <div className="mt-5 flex items-center gap-2 border-t border-black/15 pt-4 text-sm font-bold text-black/75" data-id="element-899" data-mp="3tkurhsgbyb8o">
                  <MapPinIcon size={17} className="text-[#ff4d17]" aria-hidden="true" data-id="element-900" data-mp="2fupxjxqfw2n" />{' '}
                  A live view would require consented profile and location
                  context, governed by the campaign’s data policy.
                </div>
              </article>

              <article className="border border-black/15 bg-white p-5 md:p-6" data-id="element-901" data-mp="h3pi8z7tvyf7">
                <div className="flex items-start justify-between gap-4" data-id="element-902" data-mp="l5b4vca5zr2l">
                  <div data-id="element-903" data-mp="l7nm1n83lxel">
                    <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-904" data-mp="7eebhj8ez5uh">
                      FEEDBACK / PROTOTYPE
                    </p>
                    <h2 className="mt-2 font-display text-3xl font-bold" data-id="element-905" data-mp="cbel244h2qqc">
                      Feedback themes in view.
                    </h2>
                  </div>
                  <MessageSquareTextIcon aria-hidden="true" data-id="element-906" data-mp="14ulhghem5vwh" />
                </div>
                <div className="mt-7 space-y-3" data-id="element-907" data-mp="1e7u4a4xfacwt">
                  {feedback.map(([theme, detail, event], index) => <motion.article key={theme} initial={reduceMotion ? false : {
                  opacity: 0,
                  x: 10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true,
                  amount: 0.5
                }} transition={{
                  delay: reduceMotion ? 0 : index * 0.05,
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1]
                }} className="border-l-4 border-[#ff4d17] bg-[#f7f5ef] p-4" data-id="element-908" data-mp="1a7a1olu2yh3a" data-mpi={index}>
                      <p className="text-sm font-extrabold" data-id="element-909" data-mp="155kujh9zankh">{theme}</p>
                      <p className="mt-2 text-xs font-bold text-black/70" data-id="element-910" data-mp="106zq15np1hq8">
                        {detail}
                      </p>
                      <p className="mt-2 text-[10px] font-extrabold tracking-[0.1em] text-black/55" data-id="element-911" data-mp="2gonk9hzt5xt9">
                        ILLUSTRATIVE THEME · {event.toUpperCase()}
                      </p>
                    </motion.article>)}
                </div>
              </article>
            </section>

            <section className="mt-7 border border-black/15 bg-white" aria-labelledby="lineage-heading" data-id="element-912" data-mp="dxsybd3xvnjw">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/15 px-5 py-5" data-id="element-913" data-mp="ixff1wiz7lxg">
                <div data-id="element-914" data-mp="spbkjsne6ebp">
                  <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-915" data-mp="so08l5jsm09i">
                    DATA DICTIONARY / LINEAGE
                  </p>
                  <h2 id="lineage-heading" className="mt-2 font-display text-3xl font-bold" data-id="element-916" data-mp="11pqbb7j4klml">
                    What each visual would be built from.
                  </h2>
                </div>
                <QrCodeIcon aria-hidden="true" data-id="element-917" data-mp="16mgpfpbdd75d" />
              </div>
              <div className="grid divide-y divide-black/10 md:grid-cols-2 md:divide-x md:divide-y-0" data-id="element-918" data-mp="33inxikiecfyk">
                {[['EXPERIENCE STARTED', 'A participant begins a campaign interaction.', 'Timestamped experience-start event.'], ['PRODUCT CAUGHT', 'A participant completes the catch interaction.', 'Campaign-linked catch event.'], ['REDEMPTION', 'A participating location confirms collection.', 'Redemption scan or staff-confirmed event.'], ['EXPERIENCE SCORE', 'A participant submits a response after the experience.', 'Feedback-response event with campaign context.']].map(([name, description, lineage]) => <article key={name} className="p-5" data-id="element-919" data-mp="3jv0wulhwnj6n">
                    <p className="text-xs font-extrabold tracking-[0.1em] text-[#ff4d17]" data-id="element-920" data-mp="3evy1jsrq8rbv">
                      {name}
                    </p>
                    <p className="mt-3 text-sm font-bold leading-relaxed" data-id="element-921" data-mp="39vs5m0efcinz">
                      {description}
                    </p>
                    <p className="mt-3 text-xs leading-relaxed text-black/65" data-id="element-922" data-mp="1yi7uqe983oyj">
                      LINEAGE: {lineage}
                    </p>
                  </article>)}
              </div>
            </section>

            <section className="mt-7 border border-black/15 bg-white" aria-labelledby="campaign-list-heading" data-id="element-923" data-mp="urupp09p78hr">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/15 px-5 py-5" data-id="element-924" data-mp="q0w8lygvy6dg">
                <div data-id="element-925" data-mp="l3cr8pow5dha">
                  <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-926" data-mp="l2kw1fz7hhrj">
                    CAMPAIGNS / PROTOTYPE
                  </p>
                  <h2 id="campaign-list-heading" className="mt-2 font-display text-3xl font-bold" data-id="element-927" data-mp="1drid5frol8ip">
                    All experience states.
                  </h2>
                </div>
                <ClipboardListIcon aria-hidden="true" data-id="element-928" data-mp="14q21z5t1l2df" />
              </div>
              <div className="divide-y divide-black/10" data-id="element-929" data-mp="14seqk1tb2azg">
                {campaigns.map(([name, state, place]) => <div key={name} className="grid gap-2 px-5 py-4 sm:grid-cols-[1fr_auto_auto] sm:items-center" data-id="element-930" data-mp="ztbqyww3gwyv">
                    <div data-id="element-931" data-mp="3khg7rab9yhoy">
                      <p className="text-sm font-extrabold" data-id="element-932" data-mp="3kh6qcb4apiuv">{name}</p>
                      <p className="mt-1 text-xs font-bold text-black/60" data-id="element-933" data-mp="1zze2ncxn2301">
                        ILLUSTRATIVE CAMPAIGN · {place.toUpperCase()}
                      </p>
                    </div>
                    <span className="w-fit border border-black/20 px-2 py-1 text-[10px] font-extrabold" data-id="element-934" data-mp="2903u64fv977y">
                      {state}
                    </span>
                    <span className="text-xs font-bold text-black/55" data-id="element-935" data-mp="2dsdg79dv1n4y">
                      DEMO STATE
                    </span>
                  </div>)}
              </div>
            </section>

            <ResearchSource sourceId="DP-01" status="illustrative" title="Brand workspace reporting model" summary="The dashboard values, charts, audience groups, feedback themes, campaign names, and states on this page are demo content created to explain a possible campaign-review flow." methodology="If built, each measure would require a clearly defined event, campaign identifier, timeframe, data policy, and approved reporting methodology. This prototype does not claim these events are collected today." className="mt-7" data-id="element-936" data-mp="2nnozm507rij1" />

            <p className="mt-7 flex items-center gap-2 text-xs font-bold text-black/65" data-id="element-937" data-mp="25oe21xjkuxst">
              <PackageCheckIcon size={16} className="text-[#ff4d17]" aria-hidden="true" data-id="element-938" data-mp="20pkne1sk10r2" />{' '}
              This portal is a local-only visual prototype. It does not
              represent live reporting, authenticated access, or customer
              campaign data.
            </p>
          </main>
        </div>
      </div>
    </div>;
}